interface ConfigObj {
  winURL: string;
  winNa?: string;
  winName?: string;
  winParms?: string;
  winParams?: string;
  winWidth?: number;
  winHeight?: number;
  winLeft?: number;
  winTop?: number;
}

const setBaseParams = (configObj: ConfigObj): string => {
  if (configObj.winParms) return configObj.winParms;
  if (configObj.winParams) return configObj.winParams;
  return 'hotkeys=1,location=0,menubar=1,personalbar=1,resizable=1,scrollbars=1,status=1,titlebar=1,toolbar=0';
};

const setWinWidth = (configObj: ConfigObj): number => {
  let width = 0;
  if (typeof configObj.winWidth === 'number' && configObj.winWidth !== 0) {
    width =
      configObj.winWidth < 1
        ? Math.floor(window.screen.availWidth * configObj.winWidth)
        : configObj.winWidth;
  } else {
    // default width: 75% of available screen width
    // max-width: 75% of $breakpoint_big_desktop (1320px)
    width = Math.min(
      Math.floor(window.screen.availWidth * 0.75),
      Math.floor(1320 * 0.75)
    );
  }
  return width;
};

const setWinHeight = (configObj: ConfigObj): number => {
  let height = 0;
  if (typeof configObj.winHeight === 'number' && configObj.winHeight !== 0) {
    height =
      configObj.winHeight < 1
        ? Math.floor(window.screen.availHeight * height)
        : configObj.winHeight;
  } else {
    // Default height = 80% of available screen height
    height = Math.floor(window.screen.availHeight * 0.8);
  }
  return height;
};

/**
 * This function takes in a configuration object and opens a window based on the
 * configurations.
 *
 * ConfigObject structure:
 * {
 *   winURL: "string",
 *   winNa: "string",
 *   winParms: "string",
 *   winWidth: number,
 *   winHeight: number,
 *   winLeft: number,
 *   winTop: number
 * }
 * @param {object} configObj
 */
const openWindow = (configObj: ConfigObj): void => {
  if (!configObj.winURL) {
    // eslint-disable-next-line no-console
    console.warn('openWindow(): No URL provided, no action taken');
    return;
  }

  let url = configObj.winURL;

  url.indexOf('?') === -1 ? (url += '?') : (url += '&');

  const width = setWinWidth(configObj);
  const height = setWinHeight(configObj);

  // Take the difference between availHeight and winHeight and then stagger window placement.
  // Note: The winHeight which sets height is the inside or document height. It does not take into account the borders or toolbars if present.
  // Add 30 pixels to height to accomodate both upper window area and status bar area. Also update the divisor to be smaller since range of available area to stagger windows in is smaller.
  const left =
    typeof configObj.winLeft === 'number'
      ? configObj.winLeft
      : Math.floor(
          (window.screen.availWidth - width) * ((Math.random() + 1) / 3)
        );
  const top =
    typeof configObj.winTop === 'number'
      ? configObj.winTop
      : Math.floor(
          (window.screen.availHeight - (height + 30)) *
            ((Math.random() + 1) / 4)
        );

  /**
   * "To open a new window on every call of window.open(), use the special value _blank for windowName."
   * Source: https://developer.mozilla.org/en-US/docs/Web/API/Window/open#examples
   */
  const winName = configObj.winNa || configObj.winName || '_blank';

  const baseParams = setBaseParams(configObj);

  setTimeout(() => {
    let winObj = window.open(
      url,
      winName,
      `${baseParams},width=${width},height=${height},top=${top},left=${left}`
    );
    winObj.focus();
    winObj = null;
  }, 0);
};

export default openWindow;
